/**
 * La séance **en cours**, s'il y en a une — `ARCHITECTURE_V2.md` §4.5.
 *
 * 🔴 **Une seule séance à la fois** *(31/08/2026, demandé après usage : « quand
 * on lance une séance, on ne doit pas pouvoir en lancer une autre avant de la
 * finir »)*. La règle a deux conséquences visibles, et ce hook les sert toutes
 * les deux : le démarrage se refuse (`RoutinesSection`), et une bannière
 * ramène à la séance ouverte (`App`).
 *
 * ⚠️ **`finishedAt === null` EST « en cours »**, il n'y a pas de colonne
 * `status` — la forme se lit dans les données, comme `duration_seconds IS NOT
 * NULL` dit « exercice en durée ». Ne pas ajouter de drapeau à côté : ce serait
 * une seconde vérité à tenir d'accord avec la première.
 *
 * 🔴 **Le verrou est CLIENT, jamais serveur, et c'est une décision.** Refuser
 * un `PUT /api/sessions/:clientId` parce qu'une autre séance est ouverte
 * produirait un **4xx**, or un 4xx est `failed` **définitif** pour la file
 * (§3.3). Une séance composée hors ligne et drainée trois heures plus tard
 * serait alors refusée pour un conflit qui n'existait pas au moment du geste —
 * et elle disparaîtrait, ce qui est exactement le défaut que le §8.6 existe
 * pour empêcher. Le serveur reste donc permissif ; c'est l'écran qui empêche
 * d'ouvrir la seconde, au moment où l'on peut encore le dire.
 *
 * ⚠️ **Lu dans la réplique, donc sans réseau** : `SESSIONS_KEY` porte les trente
 * plus récentes et la séance en cours en fait partie **par construction** —
 * elle a commencé aujourd'hui. En salle, où il n'y a pas de réseau, la question
 * se pose et se répond quand même.
 */

import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import type { WorkoutSessionDTO } from '../../../shared/types';
import { SESSIONS_KEY, fetchSessions } from '../api/sessions';

/**
 * 🔴 **Au-delà, une séance ouverte n'est plus « en cours » — elle est
 * OUBLIÉE.** C'est le garde-fou le plus important de ce fichier, et il répond à
 * un fait déjà consigné à l'étape 6, à propos du dernier passage : « on oublie
 * de clore une séance bien plus souvent qu'on ne s'en souvient ».
 *
 * Sans borne, une séance abandonnée il y a trois semaines afficherait une
 * bannière permanente **et interdirait d'en lancer une autre pour toujours** —
 * un blocage dont rien n'indiquerait la sortie. Le verrou existe pour empêcher
 * d'ouvrir une seconde séance pendant la première, pas pour prendre
 * l'application en otage d'un oubli.
 *
 * ⚠️ **Douze heures, et non « aujourd'hui ».** La journée aurait été le repère
 * naturel — c'est celui du front partout ailleurs (`browserToday()`) — mais
 * elle introduit une **falaise à minuit** : une séance commencée à 23 h 30
 * cesserait d'être en cours à 00 h 00, au milieu. Une fenêtre glissante depuis
 * `startedAt` n'a pas de bord, et douze heures couvrent très largement les
 * 45 à 90 minutes du §12.3 avec toutes les pauses qu'on voudra.
 *
 * ⚠️ Une séance sortie de la fenêtre n'est ni close ni supprimée : elle reste
 * un **passage précédent parfaitement réel** (étape 6), simplement plus
 * courante.
 */
const FENETRE_MS = 12 * 60 * 60 * 1000;

/**
 * Un « maintenant » GROSSIER, rafraîchi seulement au retour au premier plan.
 *
 * ⚠️ **L'instant est un ÉTAT, jamais un `Date.now()` au rendu** — c'est la
 * leçon de `useTick` à l'étape 6, et `react-hooks/purity` l'a rappelée ici au
 * premier jet : deux rendus déclenchés par autre chose rendraient deux valeurs
 * sans qu'aucun état n'ait changé.
 *
 * 🔴 **Mais pas de battement à la seconde, et surtout pas ici.** Ce hook vit
 * dans la **coque**, donc au-dessus de tous les écrans : une minuterie y ferait
 * re-rendre l'application entière une fois par seconde, pour borner une fenêtre
 * de douze heures. `visibilitychange` suffit, et il couvre le seul instant qui
 * compte — celui où l'on rouvre l'application après des heures.
 *
 * ⚠️ **Les transitions qui comptent vraiment sont pilotées par les DONNÉES**,
 * pas par l'horloge : terminer une séance change `finishedAt`, donc la
 * requête, donc le rendu. L'horloge ne sert qu'au cas de la séance oubliée.
 */
function useCoarseNow(): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const relire = () => {
      setNow(Date.now());
    };
    document.addEventListener('visibilitychange', relire);
    return () => {
      document.removeEventListener('visibilitychange', relire);
    };
  }, []);

  return now;
}

/**
 * La séance en cours, ou `null`.
 *
 * ⚠️ **La plus récemment commencée s'il y en a plusieurs.** Le cas ne devrait
 * plus se produire une fois le verrou en place, mais il existe **déjà** dans
 * les données de qui a oublié de clore une séance. Trier plutôt que prendre la
 * première évite de désigner la mauvaise.
 *
 * ⚠️ L'instant vient de `useCoarseNow`, jamais d'un `Date.now()` au rendu —
 * cf. son commentaire.
 */
export function useOpenSession(): WorkoutSessionDTO | null {
  const sessions = useQuery({ queryKey: SESSIONS_KEY, queryFn: fetchSessions });
  const maintenant = useCoarseNow();

  const limite = maintenant - FENETRE_MS;
  const ouvertes = (sessions.data ?? []).filter(
    (row) => row.finishedAt === null && row.startedAt >= limite,
  );
  if (ouvertes.length === 0) return null;

  return [...ouvertes].sort((a, b) => b.startedAt - a.startedAt)[0] ?? null;
}
